import { InteractionInterface } from './InteractionInterface.js'
import TextPuzzleRender from '../components/interactions/TextPuzzleRender.vue'

export default class TextPuzzleInteraction extends InteractionInterface {
  constructor(question, testData) {
    super(question, testData)
    this.question = question
    this.testData = testData
    this.type = 'textPuzzle'
    this.gapAnswers = {}
    this.correctAnswers = this.extractCorrectAnswers()
    this.userAnswer = null
  }

  render() {
    return {
      component: TextPuzzleRender,
      props: {
        question: this.question,  // Changed from selectedElement to question
        testData: this.testData,
        response: this.userAnswer,
        restoredAnswer: this.userAnswer,
        numOrder: null,
      }
    }
  }

  /**
   * Extract correct answers from the question data
   */
  extractCorrectAnswers() {
    const correctAnswers = {}
    if (this.question && this.question.gaps) {
      this.question.gaps.forEach(gap => {
        correctAnswers[gap.id] = gap.text
      })
    }
    return correctAnswers
  }

  /**
   * Set user response
   * @param {Object} response - The user's response containing gapAnswers
   */
  setResponse(response) {
    if (response && response.gapAnswers) {
      this.gapAnswers = { ...response.gapAnswers }
    }
  }

  /**
   * Restore previous answer (required by InteractionInterface)
   * @param {*} savedAnswer - Previously saved answer
   */
  restoreAnswer(savedAnswer) {
    this.userAnswer = savedAnswer
    if (savedAnswer && savedAnswer.gapAnswers) {
      this.gapAnswers = { ...savedAnswer.gapAnswers }
    }
  }

  /**
   * Get current response
   * @returns {Object} Current response object
   */
  getResponse() {
    return {
      gapAnswers: { ...this.gapAnswers },
      timestamp: new Date().toISOString(),
      type: this.type
    }
  }

  /**
   * Validate if the response is complete
   * @returns {boolean} True if all gaps are filled
   */
  isComplete() {
    if (!this.question || !this.question.gaps) {
      return true
    }

    const totalGaps = this.question.gaps.length
    const filledGaps = Object.keys(this.gapAnswers).length

    return filledGaps === totalGaps
  }

  /**
   * Calculate score based on correct answers
   * @returns {Object} Score details
   */
  calculateScore() {
    if (!this.question || !this.question.gaps) {
      return {
        score: 0,
        maxScore: 0,
        details: {}
      }
    }

    let totalScore = 0
    let maxScore = 0
    const details = {}

    this.question.gaps.forEach(gap => {
      const userAnswer = this.gapAnswers[gap.id]
      const correctAnswer = gap.text
      const pointsPerCorrect = this.question.pointsPerCorrect || 1
      const penaltyPerIncorrect = this.question.penaltyPerIncorrect || 0

      maxScore += pointsPerCorrect

      if (userAnswer) {
        if (this.isAnswerCorrect(userAnswer, correctAnswer)) {
          totalScore += pointsPerCorrect
          details[gap.id] = {
            userAnswer,
            correctAnswer,
            isCorrect: true,
            score: pointsPerCorrect
          }
        } else {
          totalScore -= penaltyPerIncorrect
          details[gap.id] = {
            userAnswer,
            correctAnswer,
            isCorrect: false,
            score: -penaltyPerIncorrect
          }
        }
      } else {
        details[gap.id] = {
          userAnswer: null,
          correctAnswer,
          isCorrect: false,
          score: 0
        }
      }
    })

    // Ensure score doesn't go below 0
    totalScore = Math.max(0, totalScore)

    return {
      score: totalScore,
      maxScore,
      percentage: maxScore > 0 ? (totalScore / maxScore) * 100 : 0,
      details,
      completionRate: this.getCompletionRate()
    }
  }

  /**
   * Check if user answer matches correct answer
   * @param {string} userAnswer - User's answer
   * @param {string} correctAnswer - Correct answer
   * @returns {boolean} True if answers match
   */
  isAnswerCorrect(userAnswer, correctAnswer) {
    if (!userAnswer || !correctAnswer) return false
    
    // Normalize strings for comparison (trim whitespace, case insensitive)
    const normalizedUser = userAnswer.toString().trim().toLowerCase()
    const normalizedCorrect = correctAnswer.toString().trim().toLowerCase()
    
    return normalizedUser === normalizedCorrect
  }

  /**
   * Get completion rate
   * @returns {number} Percentage of gaps filled (0-100)
   */
  getCompletionRate() {
    if (!this.question || !this.question.gaps) {
      return 100
    }

    const totalGaps = this.question.gaps.length
    const filledGaps = Object.keys(this.gapAnswers).length

    return totalGaps > 0 ? (filledGaps / totalGaps) * 100 : 100
  }

  /**
   * Get detailed results for reporting
   * @returns {Object} Detailed results
   */
  getDetailedResults() {
    const scoreData = this.calculateScore()
    
    return {
      type: this.type,
      elementId: this.question.el_ID,
      response: this.getResponse(),
      score: scoreData.score,
      maxScore: scoreData.maxScore,
      percentage: scoreData.percentage,
      isComplete: this.isComplete(),
      completionRate: scoreData.completionRate,
      correctAnswers: this.correctAnswers,
      gapDetails: scoreData.details,
      totalGaps: this.question?.gaps?.length || 0,
      correctGaps: Object.values(scoreData.details).filter(detail => detail.isCorrect).length,
      timestamp: new Date().toISOString()
    }
  }

  /**
   * Reset the interaction to initial state
   */
  reset() {
    this.gapAnswers = {}
  }

  /**
   * Get hints or feedback for the interaction
   * @returns {string|null} Hint text if available
   */
  getHint() {
    return this.question?.tips || null
  }

  /**
   * Get progress information
   * @returns {Object} Progress details
   */
  getProgress() {
    const totalGaps = this.question?.gaps?.length || 0
    const filledGaps = Object.keys(this.gapAnswers).length
    
    return {
      completed: filledGaps,
      total: totalGaps,
      percentage: this.getCompletionRate(),
      isComplete: this.isComplete()
    }
  }

  /**
   * Export response for external systems
   * @returns {Object} Formatted response for export
   */
  exportResponse() {
    const results = this.getDetailedResults()
    
    return {
      interactionType: 'textPuzzle',
      elementId: this.question.el_ID,
      responses: Object.entries(this.gapAnswers).map(([gapId, answer]) => {
        const gap = this.question.gaps.find(g => g.id === gapId)
        return {
          gapId,
          gapPosition: gap ? this.question.gaps.indexOf(gap) + 1 : null,
          userAnswer: answer,
          correctAnswer: this.correctAnswers[gapId],
          isCorrect: this.isAnswerCorrect(answer, this.correctAnswers[gapId])
        }
      }),
      summary: {
        totalGaps: results.totalGaps,
        correctGaps: results.correctGaps,
        score: results.score,
        maxScore: results.maxScore,
        percentage: results.percentage,
        completionRate: results.completionRate
      },
      timestamp: results.timestamp
    }
  }
}